import { cva, type VariantProps } from 'class-variance-authority';
import { SidebarCollapse } from 'iconoir-react';
import { Slot } from 'radix-ui';
import * as React from 'react';

import { Button } from '@savgroup-front-common/primitive/ui/button';
import { Input } from '@savgroup-front-common/primitive/ui/input';
import { Separator } from '@savgroup-front-common/primitive/ui/separator';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@savgroup-front-common/primitive/ui/sheet';
import { Skeleton } from '@savgroup-front-common/primitive/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@savgroup-front-common/primitive/ui/tooltip';
import { useIsMobile } from '@savgroup-front-common/primitive/hooks/use-mobile';
import { cn } from '@savgroup-front-common/primitive/lib/utils';

import { spacing } from '../helpers/spacing';

const SIDEBAR_COOKIE_NAME = 'sidebar_state';
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
const SIDEBAR_WIDTH = spacing(64);
const SIDEBAR_WIDTH_MOBILE = spacing(72);
const SIDEBAR_WIDTH_ICON = spacing(11);
const SIDEBAR_KEYBOARD_SHORTCUT = 'b';

type SidebarContextProps = {
  state: 'expanded' | 'collapsed';
  open: boolean;
  setOpen: (open: boolean) => void;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  isMobile: boolean;
  toggleSidebar: () => void;
};

const SidebarContext = React.createContext<SidebarContextProps | null>(null);

export function useSidebar() {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider.');
  }

  return context;
}

export function SidebarProvider({
  defaultOpen = true,
  open: openProp,
  onOpenChange: setOpenProp,
  className,
  style,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const isMobile = useIsMobile();
  const [openMobile, setOpenMobile] = React.useState(false);

  const [internalOpen, setInternalOpen] = React.useState(defaultOpen);
  const open = openProp ?? internalOpen;
  const setOpen = React.useCallback(
    (value: boolean | ((value: boolean) => boolean)) => {
      const openState = typeof value === 'function' ? value(open) : value;
      if (setOpenProp) {
        setOpenProp(openState);
      } else {
        setInternalOpen(openState);
      }

      document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
    },
    [setOpenProp, open],
  );

  const toggleSidebar = React.useCallback(() => {
    return isMobile ? setOpenMobile((open) => !open) : setOpen((open) => !open);
  }, [isMobile, setOpen, setOpenMobile]);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key === SIDEBAR_KEYBOARD_SHORTCUT &&
        (event.metaKey || event.ctrlKey)
      ) {
        event.preventDefault();
        toggleSidebar();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleSidebar]);

  const state = open ? 'expanded' : 'collapsed';

  const contextValue = React.useMemo<SidebarContextProps>(
    () => ({
      state,
      open,
      setOpen,
      isMobile,
      openMobile,
      setOpenMobile,
      toggleSidebar,
    }),
    [state, open, setOpen, isMobile, openMobile, setOpenMobile, toggleSidebar],
  );

  return (
    <SidebarContext.Provider value={contextValue}>
      <div
        data-slot="sidebar-wrapper"
        style={
          {
            '--sidebar-width': SIDEBAR_WIDTH,
            '--sidebar-width-icon': SIDEBAR_WIDTH_ICON,
            ...style,
          } as React.CSSProperties
        }
        className={cn(
          'revers-sidebar-wrapper',
          'group/sidebar-wrapper flex min-h-svh w-full has-data-[variant=inset]:bg-surface-muted-default',
          className,
        )}
        {...props}
      >
        {children}
      </div>
    </SidebarContext.Provider>
  );
}

export function Sidebar({
  side = 'left',
  variant = 'sidebar',
  collapsible = 'offcanvas',
  className,
  children,
  dir,
  ...props
}: React.ComponentProps<'div'> & {
  side?: 'left' | 'right';
  variant?: 'sidebar' | 'floating' | 'inset';
  collapsible?: 'offcanvas' | 'icon' | 'none';
}) {
  const { isMobile, state, openMobile, setOpenMobile } = useSidebar();

  if (collapsible === 'none') {
    return (
      <div
        data-slot="sidebar"
        className={cn(
          'revers-sidebar',
          'flex h-full w-(--sidebar-width) flex-col bg-surface-muted-default stext-base-default',
          className,
        )}
        {...props}
      >
        {children}
      </div>
    );
  }

  if (isMobile) {
    return (
      <Sheet open={openMobile} onOpenChange={setOpenMobile} {...props}>
        <SheetContent
          dir={dir}
          data-sidebar="sidebar"
          data-slot="sidebar"
          data-mobile="true"
          className="revers-sidebar w-(--sidebar-width) bg-surface-muted-default p-0 stext-base-default [&>button]:hidden"
          style={
            {
              '--sidebar-width': SIDEBAR_WIDTH_MOBILE,
            } as React.CSSProperties
          }
          side={side}
        >
          <SheetHeader className="sr-only">
            <SheetTitle>Sidebar</SheetTitle>
            <SheetDescription>Displays the mobile sidebar.</SheetDescription>
          </SheetHeader>
          <div className="flex h-full w-full flex-col">{children}</div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <div
      className="revers-sidebar group peer hidden stext-base-default data-[state=collapsed]:stext-base-default md:block"
      data-state={state}
      data-collapsible={state === 'collapsed' ? collapsible : ''}
      data-variant={variant}
      data-side={side}
      data-slot="sidebar"
    >
      {/* This is what handles the sidebar gap on desktop */}
      <div
        data-slot="sidebar-gap"
        className={cn(
          'revers-sidebar-gap',
          'relative w-(--sidebar-width) bg-transparent transition-[width] duration-200 ease-linear',
          'group-data-[collapsible=offcanvas]:w-0',
          'group-data-[side=right]:rotate-180',
          variant === 'floating' || variant === 'inset'
            ? 'group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4)))]'
            : 'group-data-[collapsible=icon]:w-(--sidebar-width-icon)',
        )}
      />
      <div
        data-slot="sidebar-container"
        data-side={side}
        className={cn(
          'revers-sidebar-container',
          'fixed inset-y-0 z-10 hidden h-svh w-(--sidebar-width) transition-[left,right,width] duration-200 ease-linear data-[side=left]:left-0 data-[side=left]:group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)] data-[side=right]:right-0 data-[side=right]:group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)] md:flex',
          // Adjust the padding for floating and inset variants.
          variant === 'floating' || variant === 'inset'
            ? 'p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4))+2px)]'
            : 'group-data-[collapsible=icon]:w-(--sidebar-width-icon) group-data-[side=left]:border-r group-data-[side=right]:border-l group-data-[side=left]:border-border-base-default group-data-[side=right]:border-border-base-default group-data-[state=collapsed]:group-data-[side=left]:border-border-base-default group-data-[state=collapsed]:group-data-[side=right]:border-border-base-default',
          className,
        )}
        {...props}
      >
        <div
          data-sidebar="sidebar"
          data-slot="sidebar-inner"
          className="revers-sidebar-inner flex size-full flex-col bg-surface-muted-default group-data-[state=collapsed]:bg-surface-muted-default group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:shadow-sm group-data-[variant=floating]:ring-1 group-data-[variant=floating]:ring-border-base-default group-data-[state=collapsed]:group-data-[variant=floating]:ring-border-base-default"
        >
          {children}
        </div>
      </div>
    </div>
  );
}

export function SidebarTrigger({
  className,
  onClick,
  ...props
}: React.ComponentProps<typeof Button>) {
  const { toggleSidebar } = useSidebar();

  return (
    <Button
      data-sidebar="trigger"
      data-slot="sidebar-trigger"
      variant="ghost"
      size="icon-sm"
      className={cn('revers-sidebar-trigger', className)}
      onClick={(event) => {
        onClick?.(event);
        toggleSidebar();
      }}
      {...props}
    >
      <SidebarCollapse />
      <span className="sr-only">Toggle Sidebar</span>
    </Button>
  );
}

export function SidebarRail({
  className,
  ...props
}: React.ComponentProps<'button'>) {
  const { toggleSidebar } = useSidebar();

  return (
    <button
      data-sidebar="rail"
      data-slot="sidebar-rail"
      aria-label="Toggle Sidebar"
      tabIndex={-1}
      onClick={toggleSidebar}
      title="Toggle Sidebar"
      className={cn(
        'revers-sidebar-rail',
        'absolute inset-y-0 z-20 hidden w-4 transition-all ease-linear group-data-[side=left]:-right-4 group-data-[side=right]:left-0 after:absolute after:inset-y-0 after:start-1/2 after:w-[2px] hover:after:bg-border-base-default group-data-[state=collapsed]:hover:after:bg-border-base-default sm:flex ltr:-translate-x-1/2 rtl:-translate-x-1/2',
        'in-data-[side=left]:cursor-w-resize in-data-[side=right]:cursor-e-resize',
        '[[data-side=left][data-state=collapsed]_&]:cursor-e-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize',
        'group-data-[collapsible=offcanvas]:translate-x-0 group-data-[collapsible=offcanvas]:after:left-full hover:group-data-[collapsible=offcanvas]:bg-surface-muted-default',
        '[[data-side=left][data-collapsible=offcanvas]_&]:-right-2',
        '[[data-side=right][data-collapsible=offcanvas]_&]:-left-2',
        className,
      )}
      {...props}
    />
  );
}

export function SidebarInset({
  className,
  ...props
}: React.ComponentProps<'main'>) {
  return (
    <main
      data-slot="sidebar-inset"
      className={cn(
        'revers-sidebar-inset',
        'relative flex w-full flex-1 flex-col bg-surface-base-default md:peer-data-[variant=inset]:m-2 md:peer-data-[variant=inset]:ml-0 md:peer-data-[variant=inset]:rounded-xl md:peer-data-[variant=inset]:shadow-sm md:peer-data-[variant=inset]:peer-data-[state=collapsed]:ml-2',
        className,
      )}
      {...props}
    />
  );
}

export function SidebarInput({
  className,
  ...props
}: React.ComponentProps<typeof Input>) {
  return (
    <Input
      data-slot="sidebar-input"
      data-sidebar="input"
      className={cn(
        'revers-sidebar-input',
        'h-8 w-full bg-surface-base-default shadow-none',
        className,
      )}
      {...props}
    />
  );
}

export function SidebarHeader({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-header"
      data-sidebar="header"
      className={cn(
        'revers-sidebar-header',
        'flex flex-col gap-2 p-2',
        className,
      )}
      {...props}
    />
  );
}

export function SidebarFooter({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-footer"
      data-sidebar="footer"
      className={cn(
        'revers-sidebar-footer',
        'flex flex-col gap-2 p-2',
        className,
      )}
      {...props}
    />
  );
}

export function SidebarSeparator({
  className,
  ...props
}: React.ComponentProps<typeof Separator>) {
  return (
    <Separator
      data-slot="sidebar-separator"
      data-sidebar="separator"
      className={cn(
        'revers-sidebar-separator',
        'mx-2 w-auto bg-border-base-default group-data-[state=collapsed]:bg-border-base-default',
        className,
      )}
      {...props}
    />
  );
}

export function SidebarContent({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-content"
      data-sidebar="content"
      className={cn(
        'revers-sidebar-content',
        'no-scrollbar flex min-h-0 flex-1 flex-col gap-2 overflow-auto group-data-[collapsible=icon]:overflow-hidden',
        className,
      )}
      {...props}
    />
  );
}

export function SidebarGroup({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-group"
      data-sidebar="group"
      className={cn(
        'revers-sidebar-group',
        'relative flex w-full min-w-0 flex-col p-2',
        className,
      )}
      {...props}
    />
  );
}

export function SidebarGroupLabel({
  className,
  asChild = false,
  ...props
}: React.ComponentProps<'div'> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : 'div';

  return (
    <Comp
      data-slot="sidebar-group-label"
      data-sidebar="group-label"
      className={cn(
        'revers-sidebar-group-label',
        'flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium stext-muted-default group-data-[state=collapsed]:stext-muted-default ring-border-base-default group-data-[state=collapsed]:ring-border-base-default outline-hidden transition-[margin,opacity] duration-200 ease-linear group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0 focus-visible:ring-2 [&>svg]:size-5 [&>svg]:shrink-0',
        className,
      )}
      {...props}
    />
  );
}

export function SidebarGroupAction({
  className,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : 'button';

  return (
    <Comp
      data-slot="sidebar-group-action"
      data-sidebar="group-action"
      className={cn(
        'revers-sidebar-group-action',
        'absolute top-3.5 right-3 flex aspect-square w-5 items-center justify-center rounded-md p-0 stext-base-default group-data-[state=collapsed]:stext-base-default ring-border-base-default group-data-[state=collapsed]:ring-border-base-default outline-hidden transition-transform group-data-[collapsible=icon]:hidden after:absolute after:-inset-2 hover:bg-surface-primary-default hover:stext-inverse-default group-data-[state=collapsed]:hover:bg-surface-primary-default group-data-[state=collapsed]:hover:stext-inverse-default focus-visible:ring-2 md:after:hidden [&>svg]:size-5 [&>svg]:shrink-0',
        className,
      )}
      {...props}
    />
  );
}

export function SidebarGroupContent({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-group-content"
      data-sidebar="group-content"
      className={cn(
        'revers-sidebar-group-content',
        'w-full text-sm',
        className,
      )}
      {...props}
    />
  );
}

export function SidebarMenu({
  className,
  ...props
}: React.ComponentProps<'ul'>) {
  return (
    <ul
      data-slot="sidebar-menu"
      data-sidebar="menu"
      className={cn(
        'revers-sidebar-menu',
        'flex w-full min-w-0 flex-col gap-1',
        className,
      )}
      {...props}
    />
  );
}

export function SidebarMenuItem({
  className,
  ...props
}: React.ComponentProps<'li'>) {
  return (
    <li
      data-slot="sidebar-menu-item"
      data-sidebar="menu-item"
      className={cn(
        'revers-sidebar-menu-item',
        'group/menu-item relative',
        className,
      )}
      {...props}
    />
  );
}

const sidebarMenuButtonVariants = cva(
  'peer/menu-button group/menu-button flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm ring-border-base-default group-data-[state=collapsed]:ring-border-base-default outline-hidden transition-[width,height,padding] group-has-data-[sidebar=menu-action]/menu-item:pr-8 group-data-[collapsible=icon]:size-8! group-data-[collapsible=icon]:p-2! hover:bg-surface-primary-default hover:stext-inverse-default group-data-[state=collapsed]:hover:bg-surface-primary-default group-data-[state=collapsed]:hover:stext-inverse-default focus-visible:ring-2 active:bg-surface-primary-default active:stext-inverse-default group-data-[state=collapsed]:active:bg-surface-primary-default group-data-[state=collapsed]:active:stext-inverse-default disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-open:hover:bg-surface-primary-default data-open:hover:stext-inverse-default group-data-[state=collapsed]:data-open:hover:bg-surface-primary-default group-data-[state=collapsed]:data-open:hover:stext-inverse-default data-active:bg-surface-primary-default data-active:font-medium data-active:stext-inverse-default group-data-[state=collapsed]:data-active:bg-surface-primary-default group-data-[state=collapsed]:data-active:stext-inverse-default [&_svg]:size-5 [&_svg]:shrink-0 [&>span:last-child]:truncate',
  {
    variants: {
      variant: {
        default:
          'hover:bg-surface-primary-default hover:stext-inverse-default group-data-[state=collapsed]:hover:bg-surface-primary-default group-data-[state=collapsed]:hover:stext-inverse-default',
        outline:
          'bg-surface-base-default ring-1 ring-border-base-default hover:bg-surface-primary-default hover:stext-inverse-default hover:ring-surface-primary-default',
        muted:
          'stext-base-default! hover:bg-surface-base-hover! hover:stext-base-default! active:bg-surface-base-hover! active:stext-base-default! aria-[current=page]:bg-surface-selected-default! aria-[current=page]:stext-primary-default! group-data-[state=collapsed]:hover:bg-surface-base-hover! group-data-[state=collapsed]:hover:stext-base-default! group-data-[state=collapsed]:active:bg-surface-base-hover! group-data-[state=collapsed]:active:stext-base-default!',
        destructive:
          'stext-danger-default! hover:bg-surface-danger-subtle-default! hover:stext-danger-default! active:bg-surface-danger-subtle-default! active:stext-danger-default! group-data-[state=collapsed]:hover:bg-surface-danger-subtle-default! group-data-[state=collapsed]:hover:stext-danger-default! group-data-[state=collapsed]:active:bg-surface-danger-subtle-default! group-data-[state=collapsed]:active:stext-danger-default!',
      },
      size: {
        default: 'h-8 text-sm',
        sm: 'h-7 text-xs',
        lg: 'h-12 text-sm group-data-[collapsible=icon]:p-0!',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export function SidebarMenuButton({
  asChild = false,
  isActive = false,
  variant = 'default',
  size = 'default',
  tooltip,
  className,
  ...props
}: React.ComponentProps<'button'> & {
  asChild?: boolean;
  isActive?: boolean;
  tooltip?: string | React.ComponentProps<typeof TooltipContent>;
} & VariantProps<typeof sidebarMenuButtonVariants>) {
  const Comp = asChild ? Slot.Root : 'button';
  const { isMobile, state } = useSidebar();

  const button = (
    <Comp
      data-slot="sidebar-menu-button"
      data-sidebar="menu-button"
      data-size={size}
      data-active={isActive}
      className={cn(
        'revers-sidebar-menu-button',
        sidebarMenuButtonVariants({ variant, size }),
        className,
      )}
      {...props}
    />
  );

  if (!tooltip) {
    return button;
  }

  if (typeof tooltip === 'string') {
    tooltip = {
      children: tooltip,
    };
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent
        side="right"
        align="center"
        hidden={state !== 'collapsed' || isMobile}
        {...tooltip}
      />
    </Tooltip>
  );
}

export function SidebarMenuAction({
  className,
  asChild = false,
  showOnHover = false,
  ...props
}: React.ComponentProps<'button'> & {
  asChild?: boolean;
  showOnHover?: boolean;
}) {
  const Comp = asChild ? Slot.Root : 'button';

  return (
    <Comp
      data-slot="sidebar-menu-action"
      data-sidebar="menu-action"
      className={cn(
        'revers-sidebar-menu-action',
        'absolute top-1.5 right-1 flex aspect-square w-5 items-center justify-center rounded-md p-0 stext-base-default group-data-[state=collapsed]:stext-base-default ring-border-base-default group-data-[state=collapsed]:ring-border-base-default outline-hidden transition-transform group-data-[collapsible=icon]:hidden peer-hover/menu-button:stext-inverse-default group-data-[state=collapsed]:peer-hover/menu-button:stext-inverse-default peer-data-[size=default]/menu-button:top-1.5 peer-data-[size=lg]/menu-button:top-2.5 peer-data-[size=sm]/menu-button:top-1 after:absolute after:-inset-2 hover:bg-surface-primary-default hover:stext-inverse-default group-data-[state=collapsed]:hover:bg-surface-primary-default group-data-[state=collapsed]:hover:stext-inverse-default focus-visible:ring-2 md:after:hidden [&>svg]:size-5 [&>svg]:shrink-0',
        showOnHover &&
          'group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 peer-data-active/menu-button:stext-inverse-default group-data-[state=collapsed]:peer-data-active/menu-button:stext-inverse-default aria-expanded:opacity-100 md:opacity-0',
        className,
      )}
      {...props}
    />
  );
}

export function SidebarMenuBadge({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-menu-badge"
      data-sidebar="menu-badge"
      className={cn(
        'revers-sidebar-menu-badge',
        'pointer-events-none absolute right-1 flex h-5 min-w-5 items-center justify-center rounded-md px-1 text-xs font-medium stext-base-default group-data-[state=collapsed]:stext-base-default tabular-nums select-none group-data-[collapsible=icon]:hidden peer-hover/menu-button:stext-inverse-default group-data-[state=collapsed]:peer-hover/menu-button:stext-inverse-default peer-data-[size=default]/menu-button:top-1.5 peer-data-[size=lg]/menu-button:top-2.5 peer-data-[size=sm]/menu-button:top-1 peer-data-active/menu-button:stext-inverse-default group-data-[state=collapsed]:peer-data-active/menu-button:stext-inverse-default',
        className,
      )}
      {...props}
    />
  );
}

export function SidebarMenuSkeleton({
  className,
  showIcon = false,
  ...props
}: React.ComponentProps<'div'> & {
  showIcon?: boolean;
}) {
  // Random width between 50 to 90%.
  const [width] = React.useState(() => {
    return `${Math.floor(Math.random() * 40) + 50}%`;
  });

  return (
    <div
      data-slot="sidebar-menu-skeleton"
      data-sidebar="menu-skeleton"
      className={cn(
        'revers-sidebar-menu-skeleton',
        'flex h-8 items-center gap-2 rounded-md px-2',
        className,
      )}
      {...props}
    >
      {showIcon && (
        <Skeleton
          className="size-5 rounded-md"
          data-sidebar="menu-skeleton-icon"
        />
      )}
      <Skeleton
        className="h-4 max-w-(--skeleton-width) flex-1"
        data-sidebar="menu-skeleton-text"
        style={
          {
            '--skeleton-width': width,
          } as React.CSSProperties
        }
      />
    </div>
  );
}

export function SidebarMenuSub({
  className,
  ...props
}: React.ComponentProps<'ul'>) {
  return (
    <ul
      data-slot="sidebar-menu-sub"
      data-sidebar="menu-sub"
      className={cn(
        'revers-sidebar-menu-sub',
        'mx-3.5 flex min-w-0 translate-x-px flex-col gap-1 border-l border-border-base-default px-2.5 py-0.5 group-data-[collapsible=icon]:hidden',
        className,
      )}
      {...props}
    />
  );
}

export function SidebarMenuSubItem({
  className,
  ...props
}: React.ComponentProps<'li'>) {
  return (
    <li
      data-slot="sidebar-menu-sub-item"
      data-sidebar="menu-sub-item"
      className={cn(
        'revers-sidebar-menu-sub-item',
        'group/menu-sub-item relative',
        className,
      )}
      {...props}
    />
  );
}

export function SidebarMenuSubButton({
  asChild = false,
  size = 'md',
  isActive = false,
  className,
  ...props
}: React.ComponentProps<'a'> & {
  asChild?: boolean;
  size?: 'sm' | 'md';
  isActive?: boolean;
}) {
  const Comp = asChild ? Slot.Root : 'a';

  return (
    <Comp
      data-slot="sidebar-menu-sub-button"
      data-sidebar="menu-sub-button"
      data-size={size}
      data-active={isActive}
      className={cn(
        'revers-sidebar-menu-sub-button',
        'flex h-7 min-w-0 -translate-x-px items-center gap-2 overflow-hidden rounded-md px-2 stext-base-default group-data-[state=collapsed]:stext-base-default ring-border-base-default group-data-[state=collapsed]:ring-border-base-default outline-hidden group-data-[collapsible=icon]:hidden hover:bg-surface-primary-default hover:stext-inverse-default group-data-[state=collapsed]:hover:bg-surface-primary-default group-data-[state=collapsed]:hover:stext-inverse-default focus-visible:ring-2 active:bg-surface-primary-default active:stext-inverse-default group-data-[state=collapsed]:active:bg-surface-primary-default group-data-[state=collapsed]:active:stext-inverse-default disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[size=md]:text-sm data-[size=sm]:text-xs data-active:bg-surface-primary-default data-active:stext-inverse-default group-data-[state=collapsed]:data-active:bg-surface-primary-default group-data-[state=collapsed]:data-active:stext-inverse-default [&>span:last-child]:truncate [&>svg]:size-5 [&>svg]:shrink-0 [&>svg]:stext-inverse-default group-data-[state=collapsed]:[&>svg]:stext-inverse-default',
        className,
      )}
      {...props}
    />
  );
}
